import React, { useState, useMemo, useEffect, useRef } from "react";
import { database, firestore } from "../firebaseConfig";
import { ref, onValue } from "firebase/database";
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';
import "./WorldDbTable.css";
import WorldDbTableNav from './WorldDbTableNav';
import EmailAiPopup from './EmailAiPopup';
import EmailAiWebPreview from './EmailAiWebPreview';
import WhatsappSender from './WhatsappSender';
import WorldDbStatusFilter from './WorldDbStatusFilter';
import EmailPopup from './EmailPopup';
import BulkEmailPopup from './BulkEmailPopup';
import MassSpyPopup from './MassSpyPopup';
import AiCleanerPopup from './AiCleanerPopup';

// Mapping der Spaltennamen für lesbare Überschriften
const availableFields = {
  lead_firstname: "First Name",
  lead_lastname: "Last Name",
  lead_email: "Email",
  lead_telephone: "Telephone",
  lead_address: "Address",
  lead_city: "City",
  lead_postcode: "Postcode",
  lead_country: "Country",
  business_name: "Business Name",
  lead_website: "Website",
  lead_instagram: "Instagram",
  lead_linkedin: "LinkedIn",
  lead_tiktok: "TikTok",
  lead_rating: "Rating",
  lead_reviews: "Reviews",
  lead_link: "Link",
  lead_image: "Image",
  lead_description: "Description",
  lead_stars: "Stars",
  lead_type: "Type",
  lead_facebook: "Facebook",
  lead_reply: "Has Replied",
  timestamp: "Added",
  import_name: "Import"
};

// Dropdown-Optionen für den Status
const statusOptions = ["email sent"];

// Dropdown-Optionen für Type
const typeOptions = ["Small Hotel", "Big Hotel", "Airbnb"];

/**
 * Definierte Spaltenreihenfolge (Matrix):
 * 0) selectCheckbox (Neue Spalte)
 * 1) rowNumber (Zeilennummer)
 * 2) contacted
 * 3) aiMail (Neue Spalte)
 * 4) whatsapp (Neue Spalte für WhatsApp-Button)
 * 5) spy (Neue Spalte für Web-Crawling)
 * 6) business_name
 * 7) lead_description
 * 8) lead_email
 * 9) lead_telephone
 * 10) lead_website
 * 11) lead_rating
 * 12) lead_reviews
 * 13) lead_instagram
 * 14) lead_facebook
 * 15) lead_tiktok
 * 16) lead_linkedin
 * Danach werden alle restlichen Felder aus availableFields eingefügt
 * und abschließend die neue Spalte "lastEdited" angehängt.
 */
const columnOrder = [
  "selectCheckbox",
  "rowNumber",
  "contacted",
  "aiMail",
  "whatsapp",
  "spy",
  "business_name",
  "lead_description",
  "lead_email",
  "lead_telephone",
  "lead_website",
  "lead_rating",
  "lead_reviews",
  "lead_instagram",
  "lead_facebook",
  "lead_tiktok",
  "lead_linkedin",
  ...Object.keys(availableFields).filter(
    (key) =>
      ![
        "lead_type",
        "lead_description",
        "business_name",
        "lead_email",
        "lead_telephone",
        "lead_website",
        "lead_instagram",
        "lead_facebook",
        "lead_tiktok",
        "lead_linkedin",
        "lead_reply",
        "lead_rating",
        "lead_reviews"
      ].includes(key)
  ),
  "lead_type",
  "lead_reply",
  "lastEdited"
];

// Definiere explizit, welche Spalten sortierbar sein sollen
const sortableColumns = [
  "lead_rating", 
  "lead_reviews", 
  "lead_stars", 
  "business_name", 
  "lead_firstname",
  "lead_lastname",
  "lead_email",
  "lead_website",
  "lead_instagram",
  "lead_city",
  "timestamp", 
  "import_name"
];

const WorldDbTable = ({ 
  dbData, 
  columns, 
  userId, 
  setShowUploader, 
  loading,
  loadingMore,
  setSearchTerm,
  setStatusFilterMode,
  statusFilterMode,
  setImportFilterMode,
  importFilterMode,
  importOptions,
  onSortChange,
  sortField: parentSortField,
  sortDirection: parentSortDirection,
  hasMoreData,
  loadMoreData,
  displayedCount,
  totalCount,
  totalDatabaseCount,
  contactedToday,
  onEmailResult,
  noFilterResults,
  totalContactCount,
  onReload
}) => {
  const [editedData, setEditedData] = useState({});
  
  // Lokale Sortierung (wird mit der übergeordneten Sortierung synchronisiert)
  const [sortField, setSortField] = useState(parentSortField);
  const [sortDirection, setSortDirection] = useState(parentSortDirection);

  // Neuer State zur Verwaltung von Filterwerten
  const [filterConfig, setFilterConfig] = useState({});

  // Neuer State für den Statusfilter (Toggle zwischen "all", "sent" und "new")
  const [localStatusFilterMode, setLocalStatusFilterMode] = useState("Not Contacted");

  // Neuer State für den Import-Filter
  const [localImportFilterMode, setLocalImportFilterMode] = useState("all");

  // Neuer State für lokal abgerufene Daten aus Firebase
  const [localDbData, setLocalDbData] = useState(dbData);

  const [localSearchTerm, setLocalSearchTerm] = useState('');

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [emailPopupData, setEmailPopupData] = useState({
    show: false,
    rowData: null,
    index: null
  });

  // Füge einen neuen State für den ausgewählten Index hinzu
  const [selectedRowIndex, setSelectedRowIndex] = useState(0);

  // Neue States für die Mehrfachauswahl
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedRows, setSelectedRows] = useState({});
  const [lastSelectedRowId, setLastSelectedRowId] = useState(null);
  
  // Neue States für das WhatsApp-Popup
  const [isWhatsappPopupOpen, setIsWhatsappPopupOpen] = useState(false);
  const [selectedWhatsappRowIndex, setSelectedWhatsappRowIndex] = useState(0);
  
  // Referenz für den Tabellen-Container
  const tableContainerRef = useRef(null);

  // Referenz für die Scroll-Position
  const scrollPositionRef = useRef({ top: 0, left: 0 });

  // Füge einen neuen State für ungespeicherte Änderungen hinzu
  const [pendingEdits, setPendingEdits] = useState({});
  
  // State für aktives Bearbeitungsfeld
  const [activeEditField, setActiveEditField] = useState(null);

  // Füge einen neuen State für die ID-basierte Sortierung hinzu
  const [sortedIds, setSortedIds] = useState([]);
  
  // Flag für aktives Bearbeiten, um Re-Renders zu verhindern
  const [isEditing, setIsEditing] = useState(false);
  
  // Referenz für den aktuellen Bearbeitungswert
  const currentEditValue = useRef('');

  // Neue States für AI Manager
  const [isAiManagerOpen, setIsAiManagerOpen] = useState(false);
  const [isAutoSendOpen, setIsAutoSendOpen] = useState(false);
  const [bulkSelectedRows, setBulkSelectedRows] = useState([]);
  const [bulkContactsData, setBulkContactsData] = useState([]);

  // Neuer State für AI Cleaner
  const [isAiCleanerOpen, setIsAiCleanerOpen] = useState(false);
  const [cleanerResults, setCleanerResults] = useState(null);

  // Spy-Funktionalität: Speichere nur die Zeilen, die gerade analysiert werden
  const [spyLoadingRows, setSpyLoadingRows] = useState({});

  // Neue States für Mass Spy
  const [isMassSpyActive, setIsMassSpyActive] = useState(false);
  const [massSpyQueue, setMassSpyQueue] = useState([]);
  const [massSpyProgress, setMassSpyProgress] = useState(0);
  const [massSpyTotal, setMassSpyTotal] = useState(0);
  const [massSpyResults, setMassSpyResults] = useState(null);
  // Neuer State zum Speichern aller gescannten Kontakte
  const [massSpyContacts, setMassSpyContacts] = useState([]);

  // Referenz für die Tabelle
  const tableRef = useRef(null);

  // Speichert die aktuelle Scroll-Position
  const saveScrollPosition = () => {
    if (tableContainerRef.current) {
      // Aktuelle Position im lokalen Speicher speichern
      const scrollTop = tableContainerRef.current.scrollTop;
      const scrollLeft = tableContainerRef.current.scrollLeft;
      
      localStorage.setItem('worldDbTableScrollTop', scrollTop);
      localStorage.setItem('worldDbTableScrollLeft', scrollLeft);
      
      // Wichtig: scrollPositionRef.current aktualisieren
      scrollPositionRef.current = { top: scrollTop, left: scrollLeft };
    }
  };

  // Funktion für das Mausrad-Scrollen
  const handleWheel = (e) => {
    // Verhindern des Standard-Scrollverhaltens, wenn wir selbst scrollen
    if (isEditing) {
      return; // Erlaubt Scrollen in bearbeiteten Zellen
    }
  
    // Überprüfen, ob wir am Ende oder Anfang der Tabelle sind
    const container = tableContainerRef.current;
    
    if (!container) return;
    
    // Standard-Scrollverhalten zulassen, wenn Shift gehalten wird (horizontales Scrollen)
    if (e.shiftKey) return;
    
    // Speichere die aktuelle Scrollposition
    saveScrollPosition();
  };

  // Stellt die Scroll-Position beim Laden wieder her
  useEffect(() => {
    // Nur ausführen, wenn die Tabelle geladen ist
    if (tableContainerRef.current) {
      const savedScrollTop = localStorage.getItem('worldDbTableScrollTop');
      const savedScrollLeft = localStorage.getItem('worldDbTableScrollLeft');
      
      if (savedScrollTop) {
        tableContainerRef.current.scrollTop = parseInt(savedScrollTop);
      }
      
      if (savedScrollLeft) {
        tableContainerRef.current.scrollLeft = parseInt(savedScrollLeft);
      }
    }
  }, [dbData]);

  // Synchronisiere mit übergeordneter Sortierung
  useEffect(() => {
    // Nur sortieren, wenn nicht gerade bearbeitet wird und eine explizite Benutzeraktion stattgefunden hat
    if (!isEditing && sessionStorage.getItem('user_triggered_sort') === 'true') {
      // Speichere alle ausstehenden Änderungen bevor eine Sortierung stattfindet
      saveAllPendingEdits();
      
      // Speichere die aktuelle Scroll-Position
      saveScrollPosition();
      
      // Setze lokale Sortierwerte
      setSortField(parentSortField);
      setSortDirection(parentSortDirection);
      
      // Zurücksetzen des Flags
      sessionStorage.removeItem('user_triggered_sort');
      
      // Verzögert die Wiederherstellung der Scroll-Position
      setTimeout(restoreScrollPosition, 50);
    }
  }, [parentSortField, parentSortDirection]);

  // Verarbeite onEmailResult für Status-Updates - vereinfachte Implementierung
  useEffect(() => {
    // Überprüfe, ob onEmailResult eine Funktion ist
    if (typeof onEmailResult === 'function') {
      const originalOnEmailResult = onEmailResult;
      
      // Überschreibe onEmailResult mit unserer eigenen Funktion
      const enhancedOnEmailResult = (status, message) => {
        // Speichere die Scroll-Position vor jeder Aktualisierung
        saveScrollPosition();
        
        // Kontakt wurde gelöscht -> entferne sofort aus lokaler Tabelle
        if (status === 'contact-deleted' && typeof message === 'string') {
          setLocalDbData(prev => prev.filter(item => item && item.id !== message));
          // Keine Weiterleitung an originalOnEmailResult, um Toast-Fehler zu vermeiden
          restoreScrollPosition();
          return;
        }
        
        // Nur bei Status-Updates direkt die Tabelle aktualisieren
        if (status === 'status-update' && message && message.id) {
          console.log("Status-Update erhalten:", message);
          
          // Finde den betroffenen Datensatz in der lokalen Tabelle
          const rowIndex = localDbData.findIndex(item => item && item.id === message.id);
          
          // Nur fortfahren, wenn der Datensatz gefunden wurde
          if (rowIndex !== -1) {
            console.log("Datensatz gefunden, führe lokales Update durch für ID:", message.id);
            
            // Lokalen State direkt aktualisieren
            setLocalDbData(prevData => {
              // Erstelle eine neue Kopie der Daten
              const newData = [...prevData];
              
              // Aktualisiere nur diesen einen Eintrag
              newData[rowIndex] = {
                ...newData[rowIndex],
                [message.field]: message.value,
                // Wenn das Feld lead_contacted ist, aktualisiere auch lead_status
                ...(message.field === 'lead_contacted' ? { lead_status: message.value } : {}),
                // Wenn das Feld lead_status ist, aktualisiere auch lead_contacted
                ...(message.field === 'lead_status' ? { lead_contacted: message.value } : {}),
                lead_last_email: {
                  ...newData[rowIndex].lead_last_email,
                  timestamp: message.timestamp,
                  status: message.value
                },
                updated_at: message.timestamp,
                last_contact_date: new Date(message.timestamp).toISOString()
              };
              
              return newData;
            });
            
            // Aktualisiere auch den editedData-State für korrekte Anzeige
            setEditedData(prev => ({
              ...prev,
              [`${message.id}-${message.field}`]: message.value
            }));
            
            // Force re-render, um sicherzustellen, dass die Aktualisierung sofort sichtbar ist
            setTimeout(() => {
              // Trigger einen State-Update, der ein Re-render erzwingt
              setLocalDbData(prev => [...prev]);
            }, 50);
          } else {
            console.warn("Datensatz nicht gefunden für ID:", message.id);
          }
        }
        
        // Leite alle Nachrichten an die Originalfunktion weiter
        originalOnEmailResult(status, message);
        
        // Stelle die Scroll-Position wieder her
        setTimeout(restoreScrollPosition, 100);
      };
      
      // Ersetze onEmailResult
      onEmailResult = enhancedOnEmailResult;
    }
  }, [onEmailResult, localDbData]);

  // Synchronisiere die lokalen Daten mit den Props, aber nur wenn notwendig
  useEffect(() => {
    // Wenn der User gerade bearbeitet oder eine lokale Aktualisierung aktiv ist, keine Synchronisierung durchführen
    if (isEditing) {
      return;
    }
    
    // Speichere die aktuelle Scroll-Position vor jeder Aktualisierung
    saveScrollPosition();
    
    // Prüfe, ob eine explizite Benutzeraktion stattgefunden hat (Filter oder Sortierung)
    const isUserAction = sessionStorage.getItem('user_triggered_sort') === 'true' || 
                         sessionStorage.getItem('user_triggered_filter') === 'true';
    
    // Bei unterschiedlicher Anzahl von Datensätzen
    if (localDbData.length !== dbData.length) {
      // Bei einer expliziten Benutzeraktion oder größerer Änderung immer aktualisieren
      if (isUserAction || Math.abs(localDbData.length - dbData.length) > Math.max(1, localDbData.length * 0.05)) {
        // Aktualisiere den lokalen Datenbestand, aber behalte bearbeitete Einträge bei
        updateLocalData();
      }
    } else {
      // Bei gleicher Anzahl von Datensätzen nur bei expliziten Benutzeraktionen aktualisieren
      // oder bei signifikanten Änderungen wie neuen Kontaktstatus oder fehlenden/neuen IDs
      const hasSignificantChanges = isUserAction || dbData.some((propItem, idx) => {
        const localItem = localDbData[idx];
        
        // Wenn ein lokales Update stattgefunden hat, zählt es nicht als wichtige Änderung
        if (localItem && (localItem._local_status_update === true || localItem._local_update === true)) {
          return false;
        }
        
        // Prüfe nur auf wichtige Änderungen (z.B. id, lead_contacted)
        if (!localItem || 
            propItem?.id !== localItem?.id || 
            propItem?.lead_contacted !== localItem?.lead_contacted) {
          return true;
        }
        
        return false;
      });
      
      if (hasSignificantChanges) {
        // Selbe Aktualisierungslogik wie bei Längenänderung
        updateLocalData();
      }
    }
    
    // Helper-Funktion zur Aktualisierung der lokalen Daten
    function updateLocalData() {
      // Erstelle Maps für schnellen Zugriff
      const localDataById = new Map(localDbData.map(item => [item?.id, item]));
      
      // Aktualisiere die lokalen Daten, behalte bearbeitete Einträge bei
      const updatedData = dbData.map(item => {
        if (!item?.id) return item;
        
        // Prüfe, ob dieser Eintrag im lokalen State vorhanden ist
        const existingItem = localDataById.get(item.id);
        
        // Prüfe, ob ausstehende Änderungen vorliegen
        const hasPendingEdit = Object.keys(pendingEdits).some(key => 
          key.startsWith(`${item.id}-`)
        );
        
        // Bei ausstehenden Änderungen behalten wir die lokale Version
        if (hasPendingEdit && existingItem) {
          return existingItem;
        }
        return item; // Ansonsten verwende die neue Version
      });
      
      // Aktualisiere localDbData mit den neuen Daten
      setLocalDbData(updatedData);
      
      // Zurücksetzen der Flags für Benutzeraktionen
      sessionStorage.removeItem('user_triggered_sort');
      sessionStorage.removeItem('user_triggered_filter');
    }
    
    // Verzögert die Wiederherstellung der Scroll-Position
    setTimeout(restoreScrollPosition, 50);
  }, [dbData, isEditing, pendingEdits]);

  // Synchronisiere lokalen Status mit globalem Status
  useEffect(() => {
    // Nur ausführen, wenn explizit setStatusFilterMode vorhanden ist
    if (setStatusFilterMode && localStatusFilterMode !== null) {
      // Unterscheide zwischen Initialisierung und Benutzeraktion
      const isUserAction = sessionStorage.getItem('user_triggered_filter') === 'true';
      const isInitialRender = !sessionStorage.getItem('initial_status_sync');
      
      // Beim ersten Rendering oder bei expliziter Benutzeraktion
      if (isInitialRender || isUserAction) {
        // Markiere, dass die Initialisierung erfolgt ist
        if (isInitialRender) {
          sessionStorage.setItem('initial_status_sync', 'true');
        }
        
        // Hier den Parent-State aktualisieren
        setStatusFilterMode(localStatusFilterMode);
        
        // Bei expliziter Benutzeraktion
        if (isUserAction) {
          console.log("Status-Filter geändert:", localStatusFilterMode);
          // Wichtig: Entferne das Flag NACH der Aktualisierung, um die Schleife zu unterbrechen
          sessionStorage.removeItem('user_triggered_filter');
        }
      }
    }
  }, [localStatusFilterMode, setStatusFilterMode]);

  // Synchronisiere lokalen Import-Filter mit globalem Import-Filter
  useEffect(() => {
    // Nur ausführen, wenn explizit setImportFilterMode vorhanden ist
    if (setImportFilterMode && localImportFilterMode !== null) {
      // Unterscheide zwischen Initialisierung und Benutzeraktion
      const isUserAction = sessionStorage.getItem('user_triggered_filter') === 'true';
      const isInitialRender = !sessionStorage.getItem('initial_import_sync');
      
      // Beim ersten Rendering oder bei expliziter Benutzeraktion
      if (isInitialRender || isUserAction) {
        // Markiere, dass die Initialisierung erfolgt ist
        if (isInitialRender) {
          sessionStorage.setItem('initial_import_sync', 'true');
        }
        
        // Hier den Parent-State aktualisieren
        setImportFilterMode(localImportFilterMode);
        
        // Bei expliziter Benutzeraktion
        if (isUserAction) {
          console.log("Import-Filter geändert:", localImportFilterMode);
          // Wichtig: Entferne das Flag NACH der Aktualisierung, um die Schleife zu unterbrechen
          sessionStorage.removeItem('user_triggered_filter');
        }
      }
    }
  }, [localImportFilterMode, setImportFilterMode]);

  // Synchronisiere lokalen Suchbegriff mit globalem Suchbegriff - nur auf explizite Änderungen beschränken
  useEffect(() => {
    // Prüfe, ob dies eine explizite Benutzeraktion ist oder die Initialisierung
    const isInitialSync = !sessionStorage.getItem('search_initialized');
    const isUserAction = sessionStorage.getItem('user_triggered_filter') === 'true';
    
    // Nur bei Initialisierung oder expliziter Benutzeraktion synchronisieren
    if ((isInitialSync || isUserAction) && setSearchTerm && localSearchTerm !== null) {
      // Markiere, dass wir initialisiert haben
      if (isInitialSync) {
        sessionStorage.setItem('search_initialized', 'true');
      }
      
      // Synchronisiere mit globalem State
      setSearchTerm(localSearchTerm);
      
      // Setze Flag zurück, falls es gesetzt war
      if (isUserAction) {
        sessionStorage.removeItem('user_triggered_filter');
      }
    }
  }, [localSearchTerm, setSearchTerm]);

  // Event-Handler für horizontales Scrollen
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    // Verbesserter Event-Handler für Mausrad-Scrolling
    const handleWheelEvent = (e) => {
      // Wenn Shift gedrückt ist oder es ein horizontales Scroll-Event ist
      if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        // Kein preventDefault hier, damit das native horizontale Scrollen funktioniert
        return;
      }
      
      // Bei Bearbeitung oder zielgerichteten Events in Eingabefeldern nicht eingreifen
      if (isEditing || e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || 
          e.target.getAttribute('contenteditable') === 'true') {
        return;
      }
    };

    container.addEventListener('wheel', handleWheelEvent, { passive: true });

    return () => {
      container.removeEventListener('wheel', handleWheelEvent);
    };
  }, [isEditing]);

  // Berechne die Anzahl der ausgewählten Zeilen
  const selectedCount = Object.values(selectedRows).filter(Boolean).length;

  // Berechne die Statistiken für die Kopfzeile
  const totalContacts = useMemo(() => localDbData.length, [localDbData]);
  
  // Funktion zum Aktualisieren des Filters pro Spalte
  const handleFilterChange = (colKey, value) => {
    setFilterConfig((prev) => ({ ...prev, [colKey]: value }));
  };
  
  // Hilfsfunktion zum Rendern der Sortier-Icons für einen Header
  const renderSortButtons = (colKey) => {
    const isActive = sortField === colKey;
    return (
      <div className="sort-controls">
        <button 
          className={`sort-up-btn ${isActive && sortDirection === 'asc' ? 'active' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            
            // Speichere die aktuelle Scroll-Position vor der Sortierung
            saveScrollPosition();
            
            // Speichere alle ausstehenden Änderungen vor der Sortierung
            saveAllPendingEdits();
            
            // Lokale Sortierwerte setzen
            setSortField(colKey);
            setSortDirection('asc');
            
            // Flag setzen für explizite Benutzersortierung
            sessionStorage.setItem('user_triggered_sort', 'true');
            
            // Und an die Elternkomponente weitergeben
            if (onSortChange) onSortChange(colKey, 'asc');
            
            // Verzögert die Wiederherstellung der Scroll-Position
            setTimeout(restoreScrollPosition, 50);
          }}
          title="Aufsteigend sortieren"
        >
          ▲
        </button>
        <button 
          className={`sort-down-btn ${isActive && sortDirection === 'desc' ? 'active' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            
            // Speichere die aktuelle Scroll-Position vor der Sortierung
            saveScrollPosition();
            
            // Speichere alle ausstehenden Änderungen vor der Sortierung
            saveAllPendingEdits();
            
            // Lokale Sortierwerte setzen
            setSortField(colKey);
            setSortDirection('desc');
            
            // Flag setzen für explizite Benutzersortierung
            sessionStorage.setItem('user_triggered_sort', 'true');
            
            // Und an die Elternkomponente weitergeben
            if (onSortChange) onSortChange(colKey, 'desc');
            
            // Verzögert die Wiederherstellung der Scroll-Position
            setTimeout(restoreScrollPosition, 50);
          }}
          title="Absteigend sortieren"
        >
          ▼
        </button>
      </div>
    );
  };

  // Wir verwenden die definierte Reihenfolge (columnOrder) _unabhängig_ von den übergebenen Spalten
  const orderedColumns = columnOrder;

  // Funktion zum Speichern aller ausstehenden Änderungen
  const saveAllPendingEdits = () => {
    Object.entries(pendingEdits).forEach(([key, value]) => {
      const [rowId, fieldKey] = key.split('-');
      if (rowId && fieldKey) {
        saveToDatabase(rowId, fieldKey, value);
      }
    });
    // Zurücksetzen der ausstehenden Änderungen
    setPendingEdits({});
  };
  
  // Neue Funktion zum Sortieren nach IDs
  const sortDataByIds = (data, field, direction) => {
    if (!field || !direction || !Array.isArray(data)) return data;
    
    return [...data].sort((a, b) => {
      // Handle-Spezialfall für numerische Felder
      if (field === 'lead_rating' || field === 'lead_reviews' || field === 'lead_stars') {
        const aValue = parseFloat(a[field]) || 0;
        const bValue = parseFloat(b[field]) || 0;
        return direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      // Fallback für Textfelder
      const aValue = String(a[field] || '').toLowerCase();
      const bValue = String(b[field] || '').toLowerCase();
      
      if (direction === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });
  };

  // Filtere und sortiere die Zeilen basierend auf Filter- und Sortier-Konfiguration
  const displayRows = useMemo(() => {
    // Sicherstellen, dass localDbData ein Array ist
    let rows = Array.isArray(localDbData) ? [...localDbData] : [];

    // Sicherstellen, dass keine undefined oder null Werte im Array sind
    rows = rows.filter(row => row !== undefined && row !== null);
    
    // Stelle sicher, dass jede Zeile eine ID hat (auch wenn es nur der Index ist)
    rows = rows.map((row, index) => {
      // Wenn keine ID vorhanden ist, verwende den Index als ID
      if (!row.id) {
        return { ...row, id: String(index) };
      }
      return row;
    });
    
    // Prüfe, ob eine manuelle Benutzersortierung ausgelöst wurde
    const userTriggeredSort = sessionStorage.getItem('user_triggered_sort') === 'true';
    
    // Normale Sortierung anwenden, nur wenn ein Sortierfeld angegeben ist UND
    // entweder die Initialsortierung stattfindet oder der Benutzer explizit sortiert hat
    if (sortField && userTriggeredSort) {
      rows = sortDataByIds(rows, sortField, sortDirection);
      
      // Setze das Flag zurück (wichtig für nachfolgende Updates)
      if (userTriggeredSort) {
        sessionStorage.setItem('user_triggered_sort', 'false');
      }
    }
    
    return rows;
  }, [localDbData, sortField, sortDirection]);

  // Direktes Speichern ohne Debounce, überarbeitet um die eindeutige ID zu verwenden
  const handleEdit = (rowId, key, value) => {
    // Speichere die aktuelle Scroll-Position vor dem Edit
    saveScrollPosition();
    
    // Setze das Bearbeitungs-Flag, um unerwünschte Synchronisierungen zu verhindern
    setIsEditing(true);
    
    // Bestimme, ob rowId ein Index oder eine Firebase-ID ist
    let effectiveRowId = rowId;
    let rowIndex = -1;
    
    // Versuche, den Eintrag zu finden
    if (displayRows && displayRows.length > 0) {
      // Suche nach Übereinstimmung mit ID
      rowIndex = displayRows.findIndex(row => row && row.id === rowId);
      
      // Wenn keine Übereinstimmung gefunden wurde und rowId numerisch ist, versuche es als Index
      if (rowIndex === -1 && !isNaN(parseInt(rowId))) {
        const index = parseInt(rowId);
        if (index >= 0 && index < displayRows.length) {
          rowIndex = index;
          // Verwende die ID aus dem gefundenen Eintrag, falls vorhanden
          effectiveRowId = displayRows[rowIndex].id || rowId;
        }
      }
    }
    
    // Aktualisiere den lokalen Zustand für sofortiges Feedback
    setEditedData((prev) => ({
      ...prev,
      [`${effectiveRowId}-${key}`]: value,
    }));

    // Speichere die Änderung als ausstehend
    setPendingEdits(prev => ({
      ...prev,
      [`${effectiveRowId}-${key}`]: value
    }));

    // Optimistisches Update: Aktualisiere localDbData sofort ohne die gesamte Liste neu zu rendern
    setLocalDbData(prevData => {
      // Erstelle eine neue Array-Referenz, aber ändere nur den betroffenen Eintrag
      return prevData.map((row, idx) => {
        // Verwende sowohl ID- als auch Index-Vergleich
        if (
          (row && row.id === effectiveRowId) || 
          (idx === rowIndex)
        ) {
          // Stelle sicher, dass hasWhatsappButtonPressed einen Wert hat, falls es noch nicht existiert
          const hasWhatsappButtonValue = row.hasWhatsappButtonPressed !== undefined ? 
                                        row.hasWhatsappButtonPressed : 
                                        false;
                                      
          return {
            ...row,
            [key]: value,
            hasWhatsappButtonPressed: hasWhatsappButtonValue,
            lead_last_edit: {
              time: new Date().toISOString(),
              user: userId
            },
            // Markiere als lokales Update, um unerwünschte Synchronisierung zu verhindern
            _local_update: true
          };
        }
        return row;
      });
    });

    // Speichere in Firebase (asynchron und parallel zum UI-Update)
    saveToDatabase(effectiveRowId, key, value);
    
    // Stelle die Scroll-Position wieder her
    setTimeout(restoreScrollPosition, 50);
  };

  const saveToDatabase = (rowId, key, value) => {
    if (!userId) {
      console.error("Kein Benutzer angemeldet – Änderungen werden nicht gespeichert.");
      setIsEditing(false);
      return;
    }
    
    // Entferne die Änderung aus den ausstehenden Änderungen
    setPendingEdits(prev => {
      const newPending = { ...prev };
      delete newPending[`${rowId}-${key}`];
      return newPending;
    });
    
    console.log(`Speichere in Firestore: Eintrag mit ID ${rowId}, ${key} = ${value}`);
    
    // Referenz auf das genaue Dokument in Firestore
    const rowRef = doc(firestore, `World/${userId}/db/${rowId}`);
    
    // Aktualisiere die Daten
    const updates = { 
      [key]: value,
      lead_last_edit: {
        time: new Date().toISOString(),
        user: userId
      }
    };
    
    // Finde den entsprechenden Datensatz zum Überprüfen der Existenz von hasWhatsappButtonPressed
    let foundRow = null;
    
    // Suche sowohl nach ID als auch nach Index
    if (!isNaN(parseInt(rowId))) {
      // Wenn rowId numerisch ist, überprüfe den Index
      const index = parseInt(rowId);
      if (displayRows && index >= 0 && index < displayRows.length) {
        foundRow = displayRows[index];
      }
    } else {
      // Sonst suche nach der ID
      foundRow = displayRows?.find(row => row && row.id === rowId);
    }
    
    // Füge hasWhatsappButtonPressed nur hinzu, wenn das Feld nicht in der Zeile existiert
    if (!foundRow || foundRow.hasWhatsappButtonPressed === undefined) {
      updates.hasWhatsappButtonPressed = false;
    }
    
    // Verhindern, dass wir während des Speicherns neue Daten laden
    // Dieses Flag ist bereits in handleEdit gesetzt
    
    // Verwende updateDoc, um die Änderungen in Firestore zu speichern
    updateDoc(rowRef, updates)
      .then(() => {
        console.log("Erfolgreich gespeichert:", key, value);
        
        // Speichere die Scroll-Position, falls sie sich geändert hat
        saveScrollPosition();
        
        // Kurze Verzögerung, bevor wir das Bearbeitungs-Flag zurücksetzen
        setTimeout(() => {
          setIsEditing(false);
          
          // Stelle die Scroll-Position wieder her
          restoreScrollPosition();
        }, 300);
      })
      .catch((error) => {
        console.error("Fehler beim Speichern:", error);
        setIsEditing(false);
        
        // Stelle die Scroll-Position trotz Fehler wieder her
        restoreScrollPosition();
      });
  };

  // Handle status filter change - verbesserte Implementierung
  const handleStatusFilterChange = (newMode) => {
    setStatusFilterMode(newMode);
  };

  // Handle Import-Filter change - verbesserte Implementierung
  const handleImportFilterChange = (newMode) => {
    setImportFilterMode(newMode);
  };

  // Handle search input - verbesserte Implementierung
  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  // Vereinfachte Implementierung zum Wiederherstellen der Scroll-Position
  const restoreScrollPosition = () => {
    if (tableContainerRef.current) {
      // Werte aus dem Ref direkt verwenden
      const scrollTop = scrollPositionRef.current.top;
      const scrollLeft = scrollPositionRef.current.left;
      
      // Direkte Anwendung der Scroll-Position
      if (typeof scrollTop === 'number') {
        tableContainerRef.current.scrollTop = scrollTop;
      }
      
      if (typeof scrollLeft === 'number') {
        tableContainerRef.current.scrollLeft = scrollLeft;
      }
    }
  };

  // Überarbeitete WhatsApp-Funktion, die direkt einen Link öffnet
  const handleWhatsAppClick = (index) => {
    // Speichere die aktuelle Scroll-Position
    saveScrollPosition();
    
    // Prüfe, ob der Index gültig ist
    if (index >= 0 && displayRows && index < displayRows.length) {
      const rowData = displayRows[index];
      
      // Prüfe, ob die Telefonnummer vorhanden ist
      const phoneRaw = rowData.lead_telephone;
      if (!phoneRaw || phoneRaw.trim() === "") {
        alert("Keine Telefonnummer für diesen Kontakt vorhanden.");
        return;
      }
      
      // Extrahiere alle möglichen Telefonnummern
      // Wir verwenden eine Regex, um Telefonnummern zu finden (mit oder ohne Ländervorwahl)
      const phoneMatches = phoneRaw.match(/(?:\+\d{1,4}[\s-]?)?\d+(?:[\s-]?\d+)*/g) || [];
      
      if (phoneMatches.length === 0) {
        alert("Keine gültige Telefonnummer im Format gefunden.");
        return;
      }
      
      // Nehme die erste passende Nummer
      let firstValidPhone = phoneMatches[0];
      
      // Formatiere die Telefonnummer für WhatsApp (entferne alle Nicht-Ziffern außer +)
      const formattedPhone = firstValidPhone.replace(/[^\d+]/g, "");
      
      // WICHTIG: Direktes Update des localDbData für sofortige UI-Aktualisierung
      setLocalDbData(prevData => {
        return prevData.map(item => {
          if (item && item.id === rowData.id) {
            return {
              ...item,
              hasWhatsappButtonPressed: true
            };
          }
          return item;
        });
      });
      
      // Zusätzlich auch den editedData aktualisieren
      setEditedData(prevData => ({
        ...prevData,
        [`${rowData.id}-hasWhatsappButtonPressed`]: true
      }));
      
      // Direkte Aktualisierung in der Firebase-Datenbank
      if (userId && rowData.id) {
        const docRef = doc(firestore, `World/${userId}/db/${rowData.id}`);
        updateDoc(docRef, {
          hasWhatsappButtonPressed: true,
          // Füge ein Zeitstempel hinzu, um zu wissen, wann der Button gedrückt wurde
          whatsappSentTimestamp: Date.now(),
          // Auch ein Flag hinzufügen, wer die Nachricht gesendet hat
          whatsappSentBy: userId
        })
        .then(() => {
          console.log("WhatsApp-Status erfolgreich in Firestore aktualisiert");
        })
        .catch(error => {
          console.error("Fehler beim Aktualisieren des WhatsApp-Status:", error);
        });
      }
      
      // Lade die WhatsApp-Einstellungen aus der Datenbank (RTDB - wird vorerst beibehalten)
      const whatsappSettingsRef = ref(database, `/World/${userId}/settings/whatsapp`);
      
      onValue(whatsappSettingsRef, (snapshot) => {
        let whatsappMessage = "";
        let whatsappNumber = formattedPhone;
        
        // Wenn WhatsApp-Einstellungen existieren
        if (snapshot.exists()) {
          const settings = snapshot.val();
          
          // Verwende die gespeicherte Nachricht, wenn vorhanden
          if (settings.whatsappMessage) {
            // Ersetze alle Snippets im Format {{snippet}} mit den tatsächlichen Werten
            whatsappMessage = settings.whatsappMessage.replace(
              /\{\{([a-zA-Z_]+)\}\}/g,
              (match, snippet) => {
                // Versuche, den entsprechenden Wert aus den Zeildaten zu extrahieren
                const value = rowData[snippet] || "";
                return value;
              }
            );
          }
          
          // Wenn eine benutzerdefinierte WhatsApp-Nummer in den Einstellungen vorhanden ist, verwende sie
          if (settings.whatsappNumber && settings.whatsappNumber.trim() !== "") {
            whatsappNumber = settings.whatsappNumber;
          }
        } else {
          // Wenn keine Nachricht in den Einstellungen definiert ist, erstelle eine Standard-Nachricht
          const businessName = rowData.business_name || "";
          if (businessName) {
            whatsappMessage = `Hallo ${businessName}, ich habe Ihre Webseite entdeckt und würde mich gerne über eine mögliche Zusammenarbeit unterhalten.`;
          }
        }
        
        // Erstelle den WhatsApp-Link
        let whatsappUrl = `https://wa.me/${whatsappNumber}`;
        
        // Füge die Nachricht hinzu
        if (whatsappMessage) {
          whatsappUrl += `?text=${encodeURIComponent(whatsappMessage)}`;
        }
        
        // Öffne WhatsApp in einem neuen Tab
        window.open(whatsappUrl, "_blank", "noopener,noreferrer");
      }, { onlyOnce: true });
    } else {
      console.error("Versuch, einen ungültigen Datensatz zu öffnen:", index);
    }
  }

  // Ändere den handleEmailAiClick um den Index zu speichern
  const handleEmailAiClick = async (index) => {
    console.log('🎯 [EMAIL-AI-CLICK] Starting handleEmailAiClick for index:', index);
    
    const rowData = displayRows[index];
    if (!rowData) {
      console.error('🎯 [EMAIL-AI-CLICK] No rowData found for index:', index);
      return;
    }

    console.log('🎯 [EMAIL-AI-CLICK] RowData found:', {
      id: rowData.id,
      business_name: rowData.business_name,
      lead_reply: rowData.lead_reply,
      hasReplied: rowData.lead_reply === true
    });

    // Prüfe, ob der Lead geantwortet hat
    const hasReplied = rowData.lead_reply === true;
    console.log('🎯 [EMAIL-AI-CLICK] Has replied status:', hasReplied);

    let selectedTopic = null;

    if (hasReplied) {
      console.log('🎯 [EMAIL-AI-CLICK] Lead has replied, checking for AI tags...');
      
      // Prüfe, ob Tags vorhanden sind
      if (rowData.tags && typeof rowData.tags === 'object') {
        console.log('🎯 [EMAIL-AI-CLICK] Tags found in rowData:', rowData.tags);
        
        // Konvertiere Tags-Objekt zu Array und sortiere nach appliedAt (neueste zuerst)
        const tagsArray = Object.entries(rowData.tags).map(([tagId, tagData]) => ({
          id: tagId,
          ...tagData
        }));
        
        console.log('🎯 [EMAIL-AI-CLICK] Tags array before sorting:', tagsArray);
        
        // Sortiere nach appliedAt Datum (neueste zuerst)
        tagsArray.sort((a, b) => {
          const dateA = new Date(a.appliedAt || 0);
          const dateB = new Date(b.appliedAt || 0);
          return dateB - dateA; // Neueste zuerst
        });
        
        console.log('🎯 [EMAIL-AI-CLICK] Tags array after sorting:', tagsArray);
        
        if (tagsArray.length > 0) {
          const newestTag = tagsArray[0];
          console.log('🎯 [EMAIL-AI-CLICK] Newest tag found:', newestTag);
          
          // Prüfe, ob der neueste Tag ein selectedTopic hat
          if (newestTag.selectedTopic) {
            selectedTopic = newestTag.selectedTopic;
            console.log('🎯 [EMAIL-AI-CLICK] ✅ Selected topic from newest tag:', selectedTopic);
          } else {
            console.log('🎯 [EMAIL-AI-CLICK] ⚠️ Newest tag has no selectedTopic property, loading from AI settings...');
            
            // Lade das selectedTopic aus den AI Tag-Einstellungen
            try {
              const aiTagsRef = ref(database, `/World/${userId}/settings/aiTags/${newestTag.id}`);
              const aiTagSnapshot = await new Promise((resolve) => {
                onValue(aiTagsRef, resolve, { onlyOnce: true });
              });
              
              if (aiTagSnapshot.exists()) {
                const aiTagData = aiTagSnapshot.val();
                console.log('🎯 [EMAIL-AI-CLICK] AI Tag settings loaded:', aiTagData);
                
                if (aiTagData.selectedTopic) {
                  selectedTopic = aiTagData.selectedTopic;
                  console.log('🎯 [EMAIL-AI-CLICK] ✅ Selected topic from AI settings:', selectedTopic);
                } else {
                  console.log('🎯 [EMAIL-AI-CLICK] ⚠️ No selectedTopic in AI settings either');
                }
              } else {
                console.log('🎯 [EMAIL-AI-CLICK] ⚠️ AI Tag settings not found for ID:', newestTag.id);
              }
            } catch (error) {
              console.error('🎯 [EMAIL-AI-CLICK] Error loading AI Tag settings:', error);
            }
          }
        } else {
          console.log('🎯 [EMAIL-AI-CLICK] ⚠️ No tags found in tags array');
        }
      } else {
        console.log('🎯 [EMAIL-AI-CLICK] ⚠️ No tags found in rowData or tags is not an object');
        console.log('🎯 [EMAIL-AI-CLICK] rowData.tags type:', typeof rowData.tags);
        console.log('🎯 [EMAIL-AI-CLICK] rowData.tags value:', rowData.tags);
      }
    } else {
      console.log('🎯 [EMAIL-AI-CLICK] Lead has not replied, using default topic logic');
    }

    // Fallback: Prüfe auch die alte aiTags Struktur (falls noch vorhanden)
    if (!selectedTopic && rowData.aiTags && Array.isArray(rowData.aiTags) && rowData.aiTags.length > 0) {
      console.log('🎯 [EMAIL-AI-CLICK] Checking legacy aiTags structure:', rowData.aiTags);
      const lastTag = rowData.aiTags[rowData.aiTags.length - 1];
      if (lastTag.selectedTopic) {
        selectedTopic = lastTag.selectedTopic;
        console.log('🎯 [EMAIL-AI-CLICK] ✅ Selected topic from legacy aiTags:', selectedTopic);
      }
    }

    console.log('🎯 [EMAIL-AI-CLICK] Final selected topic:', selectedTopic || 'null (will use default)');

    // Öffne das E-Mail-Popup mit dem vorausgewählten Topic
    const popupData = {
      show: true,
      rowData: {
        ...rowData,
        selectedTopic: selectedTopic // Füge das selectedTopic hinzu
      },
      index
    };
    
    console.log('🎯 [EMAIL-AI-CLICK] Opening email popup with data:', {
      hasSelectedTopic: !!selectedTopic,
      selectedTopic: selectedTopic,
      businessName: rowData.business_name
    });
    
    setEmailPopupData(popupData);
  };

  const closePopup = () => {
    // Speichere die aktuelle Scroll-Position vor dem Schließen
    saveScrollPosition();
    
    // Schließe das Popup
    setIsPopupOpen(false);
    
    // Stelle die Scroll-Position wieder her
    setTimeout(() => {
      restoreScrollPosition();
    }, 50);
  };

  // Funktion zum Umschalten des Auswahlmodus
  const toggleSelectMode = () => {
    setIsSelectMode(!isSelectMode);
    // Zurücksetzen der Auswahl beim Umschalten
    if (isSelectMode) {
      setSelectedRows({});
    }
  };

  // Funktion zum Umschalten der Auswahl einer Zeile
  const toggleRowSelection = (rowId, isShiftKey = false) => {
    // Wenn Shift gedrückt ist und bereits eine Zeile ausgewählt wurde
    if (isShiftKey && lastSelectedRowId !== null) {
      // Finde aktuelle Indizes in der angezeigten Tabelle
      const lastSelectedIndex = displayRows.findIndex(row => row.id === lastSelectedRowId);
      const currentIndex = displayRows.findIndex(row => row.id === rowId);
      
      // Wenn beide Indizes gültig sind
      if (lastSelectedIndex !== -1 && currentIndex !== -1) {
        console.log("Shift-Select von Index", lastSelectedIndex, "zu", currentIndex);
        
        // Bestimme Start- und Endindex
        const startIdx = Math.min(lastSelectedIndex, currentIndex);
        const endIdx = Math.max(lastSelectedIndex, currentIndex);
        
        // Neues Auswahlset erstellen
        const newSelectedRows = { ...selectedRows };
        
        // Wähle alle Zeilen im Bereich aus
        for (let i = startIdx; i <= endIdx; i++) {
          if (displayRows[i] && displayRows[i].id) {
            newSelectedRows[displayRows[i].id] = true;
          }
        }
        
        // Aktualisiere den State
        setSelectedRows(newSelectedRows);
      }
    } else {
      // Normales Umschalten ohne Shift
      const newValue = !selectedRows[rowId];
      setSelectedRows(prev => ({
        ...prev,
        [rowId]: newValue
      }));
      
      // Aktualisiere die letzte ausgewählte Zeile, aber nur wenn die Checkbox aktiviert wird
      if (newValue) {
        setLastSelectedRowId(rowId);
      }
    }
  };

  // Funktion zum Löschen der ausgewählten Zeilen
  const handleDeleteSelected = async () => {
    if (selectedCount === 0 || !userId) return;

    if (!window.confirm(`Are you sure you want to delete ${selectedCount} contacts?`)) {
      return;
    }

    const idsToDelete = Object.entries(selectedRows)
      .filter(([_, isSelected]) => isSelected)
      .map(([id, _]) => id);

    // Use Firestore to delete documents
    const deletePromises = idsToDelete.map(id => {
      const docRef = doc(firestore, `World/${userId}/db/${id}`);
      return deleteDoc(docRef);
    });

    try {
      await Promise.all(deletePromises);
      console.log('Successfully deleted selected contacts from Firestore.');
      setSelectedRows({});
      setIsSelectMode(false);
      onReload(); // Trigger data reload
    } catch (error) {
      console.error("Error deleting contacts from Firestore:", error);
    }
  };

  // Funktion zur Anzeige des Status für jede Zeile
  const renderStatus = (rowData, rowId) => {
    // Prüfe zuerst, ob ein AI Tag vorhanden ist
    if (rowData.tags && Object.keys(rowData.tags).length > 0) {
      // Nehme den letzten Tag
      const lastTag = Object.values(rowData.tags)[Object.values(rowData.tags).length - 1];
      const statusClass = lastTag.tag.toLowerCase().replace(/\s+/g, '-');
      
      return (
        <span 
          className={`contact-status-badge status-${statusClass}`}
          style={{ backgroundColor: lastTag.color, color: '#fff' }}
        >
          {lastTag.tag}
          {lastTag.icon && (
            <img 
              src={lastTag.icon} 
              alt=""
              className="status-tag-icon"
              style={{ marginLeft: '4px', width: '14px', height: '14px' }}
            />
          )}
        </span>
      );
    }

    // Fallback auf den normalen Status, wenn kein AI Tag vorhanden ist
    const contactStatus = editedData[`${rowId}-lead_contacted`] || 
                         rowData.lead_contacted || 
                         "Not Contacted";
    const statusClass = contactStatus.toLowerCase().replace(/\s+/g, '-');
    
    // Berechne die Tage seit der letzten E-Mail
    let daysSinceLastEmail = "";
    if (rowData.lead_last_email && rowData.lead_last_email.timestamp) {
      const emailTimestamp = parseInt(rowData.lead_last_email.timestamp);
      if (!isNaN(emailTimestamp)) {
        const diffDays = Math.floor(
          (Date.now() - emailTimestamp) / (1000 * 60 * 60 * 24)
        );
        daysSinceLastEmail = `| ${diffDays} ${diffDays === 1 ? 'Day' : 'Days'}`;
      }
    }
    
    // Kürze den Status für die Anzeige
    let shortStatus = contactStatus;

    // Direktes Mapping von Status-Texten
    if (contactStatus === "First Follow-up" || contactStatus === "First Follow-Up" || contactStatus === "1. Follow-UP") {
      shortStatus = "1. Follow-up";
    } else if (contactStatus === "Second Follow-up" || contactStatus === "Second Follow-Up" || contactStatus === "2. Follow-UP") {
      shortStatus = "2. Follow-up";
    } else if (contactStatus === "Third Follow-up" || contactStatus === "Third Follow-Up" || contactStatus === "3. Follow-UP") {
      shortStatus = "3. Follow-up";
    }
    
    return (
      <span className={`contact-status-badge status-${statusClass}`}>
        {shortStatus} {daysSinceLastEmail && <span className="days-since">{daysSinceLastEmail}</span>}
      </span>
    );
  };

  // Füge eine Funktion zum Öffnen des AI Manager hinzu
  const handleOpenAiManager = () => {
    if (selectedCount === 0) return;
    
    // Sammle alle ausgewählten Kontakte
    const selectedContactIds = Object.entries(selectedRows)
      .filter(([_, isSelected]) => isSelected)
      .map(([id, _]) => id);
    
    // Erstelle eine vollständige Kopie der Kontaktdaten, unabhängig von Filtern
    const selectedContactsComplete = selectedContactIds.map(id => {
      // Suche zuerst in displayRows (gefilterte Ansicht)
      const contactInDisplay = displayRows.find(row => row.id === id);
      if (contactInDisplay) return contactInDisplay;
      
      // Falls nicht gefunden, suche in localDbData (vollständige Daten)
      const contactInFullData = localDbData.find(row => row.id === id);
      return contactInFullData;
    }).filter(Boolean); // Entferne undefined-Einträge
    
    // Filtere Kontakte ohne gültige E-Mail-Adresse heraus
    const contactsWithValidEmail = selectedContactsComplete.filter(contact => {
      const emailField = contact.lead_email || "";
      // Teile das Feld in einzelne E-Mails auf
      const emails = emailField.split(/[,;\s]+/).filter(e => e.trim() !== "");
      // Prüfe, ob mindestens eine gültige E-Mail vorhanden ist
      return emails.some(email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()));
    });
    
    // Prüfe, ob Kontakte mit gültigen E-Mails übrig bleiben
    if (contactsWithValidEmail.length === 0) {
      alert("None of the selected contacts have a valid email address. Please select at least one contact with a valid email.");
      return;
    }
    
    // Setze die ausgewählten Zeilen-IDs und vollständige Daten
    setBulkSelectedRows(contactsWithValidEmail.map(contact => contact.id));
    
    // Wichtig: Übergebe nur Kontakte mit gültigen E-Mails an BulkEmailPopup
    setBulkContactsData(contactsWithValidEmail);
    
    // Öffne den AI Manager
    setIsAiManagerOpen(true);
  };
  
  // Funktion zum Öffnen des AutoSend-Modus
  const handleOpenAutoSend = () => {
    if (selectedCount === 0) return;
    
    // Sammle alle ausgewählten Kontakte
    const selectedContactIds = Object.entries(selectedRows)
      .filter(([_, isSelected]) => isSelected)
      .map(([id, _]) => id);
    
    // Erstelle eine vollständige Kopie der Kontaktdaten, unabhängig von Filtern
    const selectedContactsComplete = selectedContactIds.map(id => {
      // Suche zuerst in displayRows (gefilterte Ansicht)
      const contactInDisplay = displayRows.find(row => row.id === id);
      if (contactInDisplay) return contactInDisplay;
      
      // Falls nicht gefunden, suche in localDbData (vollständige Daten)
      const contactInFullData = localDbData.find(row => row.id === id);
      return contactInFullData;
    }).filter(Boolean); // Entferne undefined-Einträge
    
    // Filtere Kontakte ohne gültige E-Mail-Adresse heraus
    const contactsWithValidEmail = selectedContactsComplete.filter(contact => {
      const emailField = contact.lead_email || "";
      // Teile das Feld in einzelne E-Mails auf
      const emails = emailField.split(/[,;\s]+/).filter(e => e.trim() !== "");
      // Prüfe, ob mindestens eine gültige E-Mail vorhanden ist
      return emails.some(email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()));
    });
    
    // Prüfe, ob Kontakte mit gültigen E-Mails übrig bleiben
    if (contactsWithValidEmail.length === 0) {
      alert("None of the selected contacts have a valid email address. Please select at least one contact with a valid email.");
      return;
    }
    
    // Setze die ausgewählten Zeilen-IDs und vollständige Daten
    setBulkSelectedRows(contactsWithValidEmail.map(contact => contact.id));
    setBulkContactsData(contactsWithValidEmail);
    
    // Öffne den AutoSend-Modus
    setIsAutoSendOpen(true);
  };

  // Funktion zum Öffnen des AI Cleaner
  const handleOpenAiCleaner = () => {
    if (selectedCount === 0) return;
    
    // Sammle alle ausgewählten Kontakte
    const selectedContactIds = Object.entries(selectedRows)
      .filter(([_, isSelected]) => isSelected)
      .map(([id, _]) => id);
    
    // Erstelle eine vollständige Kopie der Kontaktdaten, unabhängig von Filtern
    const selectedContactsComplete = selectedContactIds.map(id => {
      // Suche zuerst in displayRows (gefilterte Ansicht)
      const contactInDisplay = displayRows.find(row => row.id === id);
      if (contactInDisplay) return contactInDisplay;
      
      // Falls nicht gefunden, suche in localDbData (vollständige Daten)
      const contactInFullData = localDbData.find(row => row.id === id);
      return contactInFullData;
    }).filter(Boolean); // Entferne undefined-Einträge
    
    // Setze die ausgewählten Zeilen-IDs und vollständige Daten
    setBulkSelectedRows(selectedContactIds);
    setBulkContactsData(selectedContactsComplete);
    
    // Verhindere Scrollen im Hintergrund
    document.body.classList.add('ai-cleaner-open');
    
    // Öffne den AI Cleaner
    setIsAiCleanerOpen(true);
  };

  // Funktion zum Schließen des AI Managers
  const closeAiManager = () => {
    setIsAiManagerOpen(false);
    setBulkSelectedRows([]);
    setBulkContactsData([]); // Reset der vollständigen Kontaktdaten
    setSelectedRows({}); // Reset der Auswahl, damit keine Zeilen mehr ausgewählt sind
    setIsSelectMode(false); // Deaktiviere den Auswahlmodus
  };
  
  // Funktion zum Schließen des AutoSend-Modus
  const closeAutoSend = () => {
    setIsAutoSendOpen(false);
    setBulkSelectedRows([]);
    setBulkContactsData([]); // Reset der vollständigen Kontaktdaten
    setSelectedRows({}); // Reset der Auswahl, damit keine Zeilen mehr ausgewählt sind
    setIsSelectMode(false); // Deaktiviere den Auswahlmodus
  };

  // Funktion zum Schließen des AI Cleaners
  const closeAiCleaner = (results = null) => {
    setIsAiCleanerOpen(false);
    
    // Erlaube Scrollen wieder
    document.body.classList.remove('ai-cleaner-open');
    
    // Überprüfe die Ergebnisse und entscheide, wie die UI aktualisiert werden soll
    if (results) {
      // Wenn Kontakte gelöscht wurden, ist ein Neuladen der sauberste Weg.
      if (results.removed > 0) {
        onReload(); 
      } 
      // Wenn nur Umbenennungen stattfanden, reicht ein leichtes lokales Update.
      else if (results.renamed && results.renamed.length > 0) {
        const updatedLocalDbData = [...localDbData];
        results.renamed.forEach(renamedContact => {
          const index = updatedLocalDbData.findIndex(item => item.id === renamedContact.id);
          if (index !== -1) {
            updatedLocalDbData[index] = {
              ...updatedLocalDbData[index],
              business_name: renamedContact.cleaned,
              lead_description: renamedContact.lead_description
            };
          }
        });
        setLocalDbData(updatedLocalDbData);
      }
    }
    
    // Setze die Zustände für die nächste Operation zurück.
    setCleanerResults(null);
    setBulkSelectedRows([]);
    setBulkContactsData([]);
    setSelectedRows({});
    setIsSelectMode(false);
  };

  // Neue Funktion für Spy-Button-Klick
  const handleSpyClick = async (index) => {
    // Speichere die aktuelle Scroll-Position
    saveScrollPosition();
    
    // Prüfe, ob der Index gültig ist
    if (index >= 0 && displayRows && index < displayRows.length) {
      const rowData = displayRows[index];
      
      // Prüfe, ob eine Website existiert
      const website = rowData.lead_website;
      if (!website || website.trim() === "") {
        alert("Keine Website für diesen Eintrag vorhanden.");
        return;
      }
      
      // Markiere diese Zeile als "wird geladen"
      setSpyLoadingRows(prev => ({
        ...prev,
        [rowData.id]: true
      }));
      
      try {
        // Rufe die WorldWebCrawler Cloud Function auf
        const response = await fetch('https://us-central1-bonusclub-ab5e2.cloudfunctions.net/worldWebCrawler', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            website: rowData.lead_website,
            businessName: rowData.business_name || "",
            address: rowData.lead_address || "",
            rowId: rowData.id,
            userId: userId
          })
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const result = await response.json();
        console.log("Web-Crawling Ergebnis:", result);
        
        // Wenn erfolgreich, aktualisiere die Daten in der Tabelle
        if (result.success && result.formattedData) {
          const { emails, primaryEmail, description } = result.formattedData;
          
          // Aktualisiere E-Mail wenn vorhanden
          if (primaryEmail && primaryEmail.trim() !== "") {
            handleEdit(rowData.id, "lead_email", primaryEmail);
          }
          
          // Aktualisiere Beschreibung wenn vorhanden
          if (description && description.trim() !== "") {
            handleEdit(rowData.id, "lead_description", description);
          }
        }
      } catch (error) {
        console.error("Fehler beim Web-Crawling:", error);
        alert(`Fehler beim Analysieren der Website: ${error.message}`);
      } finally {
        // Entferne den Ladezustand
        setSpyLoadingRows(prev => {
          const newState = { ...prev };
          delete newState[rowData.id];
          return newState;
        });
        
        // Stelle die Scroll-Position wieder her
        setTimeout(restoreScrollPosition, 50);
      }
    }
  };

  // Funktion zum Starten des Mass Spy Prozesses
  const handleStartMassSpy = () => {
    if (selectedCount === 0) return;
    
    // Sammle alle ausgewählten Kontakte
    const selectedContactIds = Object.entries(selectedRows)
      .filter(([_, isSelected]) => isSelected)
      .map(([id, _]) => id);
    
    // Finde die kompletten Datensätze für die ausgewählten IDs
    const selectedContacts = displayRows.filter(row => 
      row && row.id && selectedContactIds.includes(row.id)
    );
    
    // Filtere nur Kontakte mit Website
    const contactsWithWebsite = selectedContacts.filter(row => 
      row.lead_website && row.lead_website.trim() !== ""
    );
    
    if (contactsWithWebsite.length === 0) {
      alert("Keiner der ausgewählten Kontakte hat eine Website-URL.");
      return;
    }
    
    // Initialisiere die Mass Spy Variablen
    setMassSpyQueue(contactsWithWebsite);
    setMassSpyTotal(contactsWithWebsite.length);
    setMassSpyProgress(0);
    setMassSpyResults({
      processedCount: 0,
      skippedCount: selectedContacts.length - contactsWithWebsite.length,
      emailsFound: 0,
      descriptionsFound: 0,
      successCount: 0,
      errorCount: 0
    });
    
    // Speichere alle Kontakte, die gescannt werden
    setMassSpyContacts(contactsWithWebsite);
    
    // Starte den Prozess
    setIsMassSpyActive(true);
  };
  
  // Verarbeitet die Mass Spy Queue asynchron
  useEffect(() => {
    // Nur ausführen, wenn Mass Spy aktiv ist und noch Elemente in der Queue sind
    if (!isMassSpyActive || massSpyQueue.length === 0) return;
    
    // Hole den nächsten Kontakt aus der Queue
    const processNextContact = async () => {
      const currentContact = massSpyQueue[0];
      const remainingQueue = massSpyQueue.slice(1);
      
      try {
        // Rufe die WorldWebCrawler Cloud Function auf
        const response = await fetch('https://us-central1-bonusclub-ab5e2.cloudfunctions.net/worldWebCrawler', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            website: currentContact.lead_website,
            businessName: currentContact.business_name || "",
            address: currentContact.lead_address || "",
            rowId: currentContact.id,
            userId: userId
          })
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const result = await response.json();
        console.log(`Mass Spy Ergebnis für ${currentContact.id}:`, result);
        
        let emailFound = false;
        let descriptionFound = false;
        
        // Wenn erfolgreich, aktualisiere die Daten in der Tabelle
        if (result.success && result.formattedData) {
          const { emails, primaryEmail, description } = result.formattedData;
          
          // Aktualisiere E-Mail wenn vorhanden
          if (primaryEmail && primaryEmail.trim() !== "") {
            handleEdit(currentContact.id, "lead_email", primaryEmail);
            emailFound = true;
            
            // Markiere den Kontakt als einen, für den eine E-Mail gefunden wurde
            currentContact._emailFound = true;
          }
          
          // Aktualisiere Beschreibung wenn vorhanden
          if (description && description.trim() !== "") {
            handleEdit(currentContact.id, "lead_description", description);
            descriptionFound = true;
          }
          
          // Aktualisiere die Ergebnisse
          setMassSpyResults(prev => ({
            ...prev,
            processedCount: prev.processedCount + 1,
            emailsFound: prev.emailsFound + (emailFound ? 1 : 0),
            descriptionsFound: prev.descriptionsFound + (descriptionFound ? 1 : 0),
            successCount: prev.successCount + 1
          }));
        } else {
          throw new Error("Keine gültigen Daten erhalten");
        }
      } catch (error) {
        console.error(`Fehler beim Mass Spy für ${currentContact.id}:`, error);
        
        // Aktualisiere die Fehlerstatistik
        setMassSpyResults(prev => ({
          ...prev,
          processedCount: prev.processedCount + 1,
          errorCount: prev.errorCount + 1
        }));
      } finally {
        // Aktualisiere den Fortschritt
        setMassSpyProgress(prev => prev + 1);
        
        // In massSpyContacts, markiere den Kontakt entsprechend
        setMassSpyContacts(prevContacts => {
          return prevContacts.map(contact => {
            if (contact.id === currentContact.id) {
              return {
                ...contact,
                _emailFound: currentContact._emailFound || false
              };
            }
            return contact;
          });
        });
        
        // Aktualisiere die Queue
        setMassSpyQueue(remainingQueue);
        
        // Wenn die Queue leer ist, beenden wir den Prozess nicht sofort,
        // damit der Benutzer den abschließenden Bericht sehen kann
        if (remainingQueue.length === 0) {
          setTimeout(() => {
            // Warte einen Moment, bevor wir deaktivieren, damit der 100% Fortschritt zu sehen ist
          }, 1000);
        }
      }
    };
    
    // Verarbeite den nächsten Kontakt
    processNextContact();
    
  }, [isMassSpyActive, massSpyQueue, userId]);
  
  // Schließen des Mass Spy Popups
  const closeMassSpy = () => {
    setIsMassSpyActive(false);
    setMassSpyQueue([]);
    setSelectedRows({});
    setIsSelectMode(false);
    // Wir löschen massSpyContacts nicht, damit wir sie später für die Löschfunktion verwenden können
  };

  // Neue Funktion: Löscht alle Einträge ohne gefundene E-Mail-Adresse
  const handleDeleteNoEmailEntries = async () => {
    if (!userId) {
      console.error("User not logged in.");
      return Promise.reject("Not logged in");
    }
    
    try {
      const contactsToDelete = massSpyContacts.filter(contact => 
        contact && !contact._emailFound
      ).map(contact => contact.id);
      
      if (contactsToDelete.length === 0) {
        return Promise.resolve();
      }
      
      const deletePromises = contactsToDelete.map(contactId => {
        const docRef = doc(firestore, `World/${userId}/db/${contactId}`);
        return deleteDoc(docRef);
      });

      await Promise.all(deletePromises);
      console.log("All entries without email have been successfully deleted from Firestore.");
      onReload();
      return Promise.resolve();
    } catch (error) {
      console.error("Error while deleting entries from Firestore:", error);
      return Promise.reject(error);
    }
  };

  // Handler-Funktion, um den AI Manager mit bereinigten Kontakten zu öffnen
  const openAiManagerWithCleanedContacts = (contactIds, contactsData) => {
    // Setze die ausgewählten Zeilen-IDs und vollständige Daten
    setBulkSelectedRows(contactIds);
    setBulkContactsData(contactsData);
    
    // Öffne den AI Manager
    setIsAiManagerOpen(true);
    
    // Lösche Zwischenergebnisse vom Cleaner
    setCleanerResults(null);
  };

  // Handler-Funktion, um AutoSend mit bereinigten Kontakten zu öffnen
  const openAutoSendWithCleanedContacts = (contactIds, contactsData) => {
    // Setze die ausgewählten Zeilen-IDs und vollständige Daten
    setBulkSelectedRows(contactIds);
    setBulkContactsData(contactsData);
    
    // Öffne den AutoSend-Modus
    setIsAutoSendOpen(true);
    
    // Lösche Zwischenergebnisse vom Cleaner
    setCleanerResults(null);
  };

  return (
    <div className="worldDbTableContainer" ref={tableContainerRef}>
      <WorldDbTableNav
        onSearch={setSearchTerm}
        statusFilterMode={statusFilterMode}
        onStatusFilterChange={setStatusFilterMode}
        importFilterMode={importFilterMode}
        onImportFilterChange={setImportFilterMode}
        importOptions={importOptions}
        setShowUploader={setShowUploader}
        totalContacts={totalContacts}
        contactedToday={contactedToday}
        isSelectMode={isSelectMode}
        toggleSelectMode={toggleSelectMode}
        selectedCount={selectedCount}
        handleDeleteSelected={handleDeleteSelected}
        totalDatabaseCount={totalDatabaseCount}
        totalContactCount={totalContactCount}
        handleOpenAiManager={handleOpenAiManager}
        handleOpenAutoSend={handleOpenAutoSend}
        handleStartMassSpy={handleStartMassSpy}
        handleOpenAiCleaner={handleOpenAiCleaner}
      />
      <div className="world-table-filters">
        {/* Hier könnten in Zukunft weitere Filter eingefügt werden */}
      </div>
      <table className="worldDbTable" onWheel={handleWheel} ref={tableRef}>
        <thead>
          <tr>
            {orderedColumns.map((colKey) => {
              // Für die Checkbox-Spalte
              if (colKey === "selectCheckbox") {
                return isSelectMode ? (
                  <th key={colKey} className="worldDbTableHeader selectCheckbox">
                    <div></div>
                  </th>
                ) : null;
              }
              
              const headerLabel =
                colKey === "rowNumber"
                  ? "Nr."
                  : colKey === "status"
                  ? "Status"
                  : colKey === "lead_type"
                  ? "Type"
                  : colKey === "lead_description"
                  ? "Description"
                  : colKey === "timestamp"
                  ? "Days"
                  : colKey === "lastEdited"
                  ? "Last Edited"
                  : colKey === "contacted"
                  ? "Lead Status"
                  : colKey === "aiMail"
                  ? "AI Mail"
                  : colKey === "spy"
                  ? "Spy"
                  : colKey === "whatsapp"
                  ? "WhatsApp"
                  : availableFields[colKey] || colKey;
                  
              // Überprüfe, ob die Spalte sortierbar ist
              const isSortable = sortableColumns.includes(colKey);
              
              return (
                <th key={colKey} className={`worldDbTableHeader ${colKey} ${isSortable ? 'sortable' : ''}`} data-column={colKey}>
                  <div className="column-header-content">
                    {headerLabel}
                  {isSortable && renderSortButtons(colKey)}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className={loading ? 'loading-data' : ''}>
          {noFilterResults ? (
            // Zeige eine "Keine Ergebnisse"-Zeile in der Tabelle an
            <tr className="no-results-row">
              <td colSpan={orderedColumns.length + (isSelectMode ? 0 : -1)} className="no-results-cell">
                <div className="no-results-content">
                  <div className="no-results-icon">
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 18a8 8 0 1 1 8-8 8.009 8.009 0 0 1-8 8zm0-14a6 6 0 1 0 0 12 6 6 0 0 0 0-12z" fill="currentColor" />
                      <path d="M21.707 20.293l-4-4a1 1 0 0 0-1.414 1.414l4 4a1 1 0 0 0 1.414-1.414z" fill="currentColor" />
                      <path d="M14 9H6a1 1 0 0 0 0 2h8a1 1 0 0 0 0-2z" fill="currentColor" />
                    </svg>
                  </div>
                  <h3 className="no-results-title">Keine passenden Kontakte gefunden</h3>
                  <p className="no-results-description">
                    Es wurden keine Kontakte gefunden, die den aktuellen Filterkriterien entsprechen.
                  </p>
                  <button 
                    className="no-results-reset-btn"
                    onClick={() => {
                      // Reset aller Filter
                      if (setSearchTerm) setSearchTerm('');
                      if (setStatusFilterMode) setStatusFilterMode('all');
                      if (setImportFilterMode) setImportFilterMode('all');
                      if (onSortChange) onSortChange('lead_last_edit.timestamp', 'desc');
                    }}
                  >
                    <svg 
                      width="14" 
                      height="14" 
                      viewBox="0 0 24 24" 
                      fill="none"
                    >
                      <path 
                        d="M17.65 6.35A7.958 7.958 0 0 0 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0 1 12 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" 
                        fill="currentColor" 
                      />
                    </svg>
                    Filter zurücksetzen
                  </button>
                </div>
              </td>
            </tr>
          ) : (
            // Wenn Ergebnisse vorhanden sind, zeige wie gewohnt die Daten an
            (displayRows && displayRows.length > 0 ? displayRows : [{}]).map((row, index) => {
              // Verwende row.id falls vorhanden, ansonsten den Index als Zeilen-ID
              const rowId = row && row.id !== undefined ? row.id : index;
              
              // Prüfe, ob dieser Lead geantwortet hat (nur lead_reply prüfen)
              const hasReplied = row && row.lead_reply === true;
              
              return (
                <tr key={index} className={`worldDbTableRow ${hasReplied ? 'has-replied' : ''}`}>
                  {orderedColumns.map((colKey) => {
                    // Für die Checkbox-Spalte
                    if (colKey === "selectCheckbox") {
                      return isSelectMode ? (
                        <td key={colKey} className="worldDbTableCell selectCheckbox">
                          <input 
                            type="checkbox" 
                            checked={!!selectedRows[rowId]} 
                            onChange={(e) => toggleRowSelection(rowId, e.nativeEvent.shiftKey)}
                          />
                        </td>
                      ) : null;
                    }
                    if (colKey === "rowNumber") {
                      return (
                        <td key={colKey} className={`worldDbTableCell rowNumber`}>
                          {index + 1}
                        </td>
                      );
                    }
                    if (colKey === "contacted") {
                      return (
                        <td key={colKey} className={`worldDbTableCell contactedCell`}>
                          {renderStatus(row, rowId)}
                        </td>
                      );
                    }
                    if (colKey === "aiMail") {
                      // Prüfe, ob mindestens eine gültige E-Mail-Adresse existiert
                      const emailField = row.lead_email || "";
                      
                      // Teile das Feld in einzelne E-Mails auf (getrennt durch Komma, Semikolon oder Leerzeichen)
                      const emails = emailField.split(/[,;\s]+/).filter(e => e.trim() !== "");
                      
                      // Prüfe, ob mindestens eine gültige E-Mail vorhanden ist
                      const hasEmail = emails.some(email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()));
                      
                      // Prüfe, ob der Lead geantwortet hat
                      const hasReplied = row && row.lead_reply === true;
                      
                      return (
                        <td key={colKey} className={`worldDbTableCell aiMail`}>
                          <button 
                            className={`ai-mail-btn ${!hasEmail ? 'disabled' : ''} ${hasReplied ? 'replied' : ''}`} 
                            onClick={() => hasEmail && handleEmailAiClick(index)}
                            disabled={!hasEmail}
                            title={!hasEmail ? "No valid email address available" : hasReplied ? "Lead has replied" : "Create AI Mail"}
                          >
                            {hasReplied ? (
                              <>
                                <span className="replied-icon">
                                  <img src="/img/done.svg" alt="Replied" />
                                </span>
                                <p style={{fontSize: '9px'}}>REPLIED</p>
                              </>
                            ) : (
                              <>
                              <div className="ai-chat-icon-container">
                              <img src="/img/mailerle.webp" alt="Chat" className="ai-chat-icon" />
                              </div>
                                AI MAIL
                              </>
                            )}
                          </button>
                        </td>
                      );
                    }
                    if (colKey === "spy") {
                      // Prüfe, ob eine Website existiert
                      const hasWebsite = row.lead_website && row.lead_website.trim() !== "";
                      // Prüfe, ob diese Zeile gerade geladen wird
                      const isLoading = spyLoadingRows[row.id] === true;
                      
                      return (
                        <td key={colKey} className={`worldDbTableCell spy`}>
                          <button 
                            className={`spy-btn ${!hasWebsite ? 'disabled' : ''} ${isLoading ? 'loading' : ''}`} 
                            onClick={() => hasWebsite && !isLoading && handleSpyClick(index)}
                            disabled={!hasWebsite || isLoading}
                            title={!hasWebsite ? "Website-URL fehlt" : isLoading ? "Analysiere Website..." : "Website analysieren"}
                          >
                            {isLoading ? (
                              <>
                                <div className="spy-spinner"></div>
                                RUN
                              </>
                            ) : (
                              <>
                                                            <div className="ai-spy-icon-container">
                                <img src="/img/binocul.svg" alt="Spy" className="spy-icon" />
                              </div>
                                SPY
                              </>
                            )}
                          </button>
                        </td>
                      );
                    }
                    if (colKey === "whatsapp") {
                      // Prüfe, ob bereits WhatsApp gesendet wurde
                      const hasWhatsappSent = row.hasWhatsappButtonPressed || false;
                      
                      return (
                        <td key={colKey} className={`worldDbTableCell whatsapp`}>
                          <button 
                            className={`whatsapp-btn ${hasWhatsappSent ? 'whatsapp-sent' : ''}`} 
                            onClick={() => handleWhatsAppClick(index)}
                          >
                            <div className="whatsapp-icon-container">
                            <img src="/img/whats.svg" alt="WhatsApp" className="whatsapp-icon" />
                            </div>
                            {hasWhatsappSent ? 'SENT' : 'WHATS'}
                            
                          </button>
                        </td>
                      );
                    }
                    // 1) Dropdown für Status
                    if (colKey === "status") {
                      const cellValue = editedData[`${rowId}-${colKey}`] ?? row[colKey] ?? "";
                      return (
                        <td
                          key={colKey}
                          className={`worldDbTableCell ${colKey}`}
                          contentEditable
                          suppressContentEditableWarning
                          onBlur={(e) =>
                            handleEdit(rowId, colKey, e.target.textContent)
                          }
                        >
                          {cellValue}
                        </td>
                      );
                    }
                    // 2) Dropdown für Type
                    else if (colKey === "lead_type") {
                      const currentValue =
                        editedData[`${rowId}-${colKey}`] ?? row[colKey] ?? "";
                      return (
                        <td key={colKey} className={`worldDbTableCell ${colKey}`}>
                          <select
                            value={currentValue}
                            onChange={(e) =>
                              handleEdit(rowId, colKey, e.target.value)
                            }
                          >
                            <option value="">Select type</option>
                            {typeOptions.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        </td>
                      );
                    }
                    // Neue Spalte: Has Replied
                    else if (colKey === "lead_reply") {
                      // Für lead_reply 
                      const hasReplied = row ? (editedData[`${rowId}-${colKey}`] ?? row[colKey] ?? false) : false;
                      
                      return (
                        <td key={colKey} className={`worldDbTableCell ${colKey}`}>
                          {hasReplied ? (
                            <span className="replied-badge">
                              <span className="replied-icon">✓</span> Replied
                            </span>
                          ) : (
                            <span className="not-replied-badge">Pending</span>
                          )}
                        </td>
                      );
                    }
                    // Neue Spalte: Timestamp als "Days"
                    else if (colKey === "timestamp") {
                      const timestampValue =
                        editedData[`${rowId}-timestamp`] ?? row.timestamp;
                      let daysAgo = "";
                      if (timestampValue) {
                        const diffDays = Math.floor(
                          (new Date() - new Date(timestampValue)) / (1000 * 60 * 60 * 24)
                        );
                        daysAgo = diffDays + " day" + (diffDays === 1 ? "" : "s");
                      }
                      return (
                        <td key={colKey} className={`worldDbTableCell ${colKey}`}>
                          {daysAgo}
                        </td>
                      );
                    }
                    // Neue Spalte: Last Edited als "hours ago"
                    else if (colKey === "lastEdited") {
                      const lastEditValue =
                        (row.lead_last_edit && row.lead_last_edit.time) || "";
                      let hoursAgo = "";
                      if (lastEditValue) {
                        const diffHours = Math.floor(
                          (new Date() - new Date(lastEditValue)) / (1000 * 60 * 60)
                        );
                        hoursAgo = diffHours + " h";
                      }
                      return (
                        <td key={colKey} className={`worldDbTableCell ${colKey}`}>
                          {hoursAgo}
                        </td>
                      );
                    }
                    // Spezielle Behandlung für Website-Spalte mit externem Link-Icon
                    else if (colKey === "lead_website") {
                      const websiteUrl = editedData[`${rowId}-${colKey}`] ?? row[colKey] ?? "";
                      
                      // Funktion zum Öffnen des Links in einem neuen Tab
                      const openWebsite = (e) => {
                        e.stopPropagation(); // Verhindert das Aktivieren der Bearbeitung
                        
                        let url = websiteUrl.trim();
                        // Prüfen, ob das Protokoll fehlt
                        if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
                          url = 'https://' + url;
                        }
                        
                        if (url) {
                          window.open(url, '_blank', 'noopener,noreferrer');
                        }
                      };
                      
                      return (
                        <td
                          key={colKey}
                          className={`worldDbTableCell ${colKey} website-cell ${activeEditField === `${rowId}-${colKey}` ? 'active-edit' : ''}`}
                        >
                          <div className="website-container">
                            <div
                              className="website-content"
                              contentEditable
                              suppressContentEditableWarning
                              onFocus={(e) => {
                                // Speichere die aktuelle Scroll-Position vor dem Setzen des Fokus
                                saveScrollPosition();
                                
                                setActiveEditField(`${rowId}-${colKey}`);
                                setIsEditing(true);
                                // Speichere den ursprünglichen Wert
                                currentEditValue.current = e.target.textContent;
                              }}
                              onBlur={(e) => {
                                // Nur speichern, wenn sich der Wert geändert hat
                                if (currentEditValue.current !== e.target.textContent) {
                                  handleEdit(rowId, colKey, e.target.textContent);
                                }
                                setActiveEditField(null);
                                // Verzögere das Zurücksetzen des Bearbeitungsstatus
                                setTimeout(() => {
                                  setIsEditing(false);
                                  // Stelle die Scroll-Position wieder her
                                  restoreScrollPosition();
                                }, 100);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                  e.preventDefault();
                                  e.target.blur();
                                } else if (e.key === 'Escape') {
                                  e.preventDefault();
                                  e.target.textContent = currentEditValue.current;
                                  e.target.blur();
                                }
                              }}
                              // Ermögliche Scroll-Verhalten im Feld
                              onWheel={(e) => {
                                // Wenn Text breiter als Container ist, Scrollen innerhalb des Feldes erlauben
                                if (e.target.scrollWidth > e.target.clientWidth) {
                                  // Nur horizontales Scrollen verhindern, wenn Shift gedrückt wird oder horizontale Bewegung
                                  if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
                                    e.stopPropagation();
                                  }
                                }
                              }}
                            >
                              {websiteUrl || "-"}
                            </div>
                            {websiteUrl && (
                              <button 
                                className="external-link-btn" 
                                onClick={openWebsite}
                                title="Website in neuem Tab öffnen"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                  <polyline points="15 3 21 3 21 9"></polyline>
                                  <line x1="10" y1="14" x2="21" y2="3"></line>
                                </svg>
                              </button>
                            )}
                          </div>
                        </td>
                      );
                    }
                    // 3) Für alle anderen Spalten: contentEditable
                    else {
                      const cellValue =
                        editedData[`${rowId}-${colKey}`] ?? row[colKey] ?? "";
                      return (
                        <td
                          key={colKey}
                          className={`worldDbTableCell ${colKey} ${activeEditField === `${rowId}-${colKey}` ? 'active-edit' : ''}`}
                          contentEditable
                          suppressContentEditableWarning
                          onFocus={(e) => {
                            // Speichere die aktuelle Scroll-Position vor dem Setzen des Fokus
                            saveScrollPosition();
                            
                            setActiveEditField(`${rowId}-${colKey}`);
                            setIsEditing(true);
                            // Speichere den ursprünglichen Wert
                            currentEditValue.current = e.target.textContent;
                          }}
                          onBlur={(e) => {
                            // Nur speichern, wenn sich der Wert geändert hat
                            if (currentEditValue.current !== e.target.textContent) {
                              handleEdit(rowId, colKey, e.target.textContent);
                            }
                            setActiveEditField(null);
                            // Verzögere das Zurücksetzen des Bearbeitungsstatus
                            setTimeout(() => {
                              setIsEditing(false);
                              // Stelle die Scroll-Position wieder her
                              restoreScrollPosition();
                            }, 100);
                          }}
                          // Verbesserte Tastatursteuerung
                          onKeyDown={(e) => {
                            // Wenn Enter gedrückt wird, beenden wir die Bearbeitung
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              e.target.blur(); // Löst onBlur aus und speichert
                            }
                            // Wenn Escape gedrückt wird, brechen wir die Bearbeitung ab
                            else if (e.key === 'Escape') {
                              e.preventDefault();
                              e.target.textContent = currentEditValue.current;
                              e.target.blur();
                            }
                          }}
                          // Ermögliche Scroll-Verhalten im Feld
                          onWheel={(e) => {
                            // Wenn Text breiter als Container ist, Scrollen innerhalb des Feldes erlauben
                            if (e.target.scrollWidth > e.target.clientWidth) {
                              // Nur horizontales Scrollen verhindern, wenn Shift gedrückt wird oder horizontale Bewegung
                              if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
                                e.stopPropagation();
                              }
                            }
                          }}
                        >
                          {cellValue ||
                            (colKey === "lead_description" ? "-" : "")}
                        </td>
                      );
                    }
                  })}
                </tr>
              );
            })
          )}
          {/* Load More Zeile */}
          {hasMoreData && !noFilterResults && (
            <tr className="load-more-row">
              <td colSpan={orderedColumns.length + (isSelectMode ? 0 : -1)} className="load-more-cell">
                <div className="table-load-more-container">
                  <button 
                    className="table-load-more-button"
                    onClick={loadMoreData}
                    disabled={loadingMore}
                  >
                    {loadingMore ? 'Loading...' : 'Load More Contacts'}
                  </button>
                  <span className="table-load-more-info">
                    {displayedCount} of {totalCount} contacts displayed
                  </span>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
      
      {/* Hier die externen Popup-Komponenten */}
      <EmailPopup 
        isPopupOpen={emailPopupData.show}
        displayRows={displayRows}
        selectedRowIndex={emailPopupData.index}
        userId={userId}
        onEmailResult={onEmailResult}
        closePopup={() => setEmailPopupData({ show: false, rowData: null, index: null })}
        rowData={emailPopupData.rowData}
      />

      <BulkEmailPopup 
        isAiManagerOpen={isAiManagerOpen}
        isAutoSendOpen={isAutoSendOpen}
        bulkSelectedRows={bulkSelectedRows}
        bulkContactsData={bulkContactsData}
        localDbData={localDbData}
        userId={userId}
        onEmailResult={onEmailResult}
        closeAiManager={closeAiManager}
        closeAutoSend={closeAutoSend}
      />

      <MassSpyPopup 
        isMassSpyActive={isMassSpyActive}
        massSpyProgress={massSpyProgress}
        massSpyTotal={massSpyTotal}
        massSpyQueue={massSpyQueue}
        massSpyResults={massSpyResults}
        closeMassSpy={closeMassSpy}
        onDeleteNoEmailEntries={handleDeleteNoEmailEntries}
      />
      
      {/* Neues AiCleanerPopup */}
      {isAiCleanerOpen && (
        <AiCleanerPopup 
          isOpen={isAiCleanerOpen}
          bulkSelectedRows={bulkSelectedRows}
          bulkContactsData={bulkContactsData}
          userId={userId}
          closeAiCleaner={closeAiCleaner}
          onOpenAiManager={openAiManagerWithCleanedContacts}
          onOpenAutoSend={openAutoSendWithCleanedContacts}
        />
      )}
    </div>
  );
};

export default WorldDbTable;

